import {classNames, React, _, StyledProps, observer, utils, Popover2, pluralize} from '@api'
import {Icon, Menu, MenuItem, Spinner, Button} from '@blueprintjs/core'
import {BuilderCardLoader, ToggleAggregationLocationButton, InlineGroupAggregateToggleButton, useGroupByGroupStore, useStores, VipLocalStore} from '@design-board/components'
import {Slider} from '@material-ui/core/'
import {MoreHoriz} from '@material-ui/icons'
import {icons} from '@stores/ui'
import {Fragment, useCallback} from 'react'
import {IGroupByQueryManagerResult, GroupByGroupStore} from './'
import * as css from './GroupByButtons.css'
import {GroupByGrouping, IGroupByStoreNode, ui} from '@stores'
import {PrimaryIconButton} from 'centric-ui-components'
import {ProductLikeDefinition} from '@design-board/graphql'

interface ButtonBarProps extends StyledProps {
  items?: Array<ProductLikeDefinition>
  className?: string
  isExpanded?: boolean
  hideChildren?: boolean
  grouping: GroupByGrouping
  info?: IGroupByQueryManagerResult
  groupName: string
  path: string[]
  count?: number
  level: number
  group: string
  specStack: Array<any>
  isUnassigned: boolean
  disabledButtons?: boolean
  storeNode?: IGroupByStoreNode
  showCollapseOnly?: boolean
  groupHeaderTitle?: any
  levelOfDetail?: number
  groupStore: GroupByGroupStore
}

class GroupByButtonsStore extends VipLocalStore {

}

export const GroupByButtons = observer<ButtonBarProps>(function GroupByButtons({
  children, hideChildren, path, className,
  level, info, group,
  isExpanded, grouping, specStack, isUnassigned, items,
  disabledButtons, showCollapseOnly, storeNode, groupHeaderTitle, groupName, classes, count,
  groupStore: {isCard, isFlat, showingAggregateDetailsinSidebar},
  groupStore,
  ...props
}) {

  const onClick = useCallback((e) => {
    // Avoid parents from hearing about this and thinking the user clicked on card whitespace
    e.preventDefault()
    e.stopPropagation()
  }, [])

  return (showCollapseOnly) ? (<Fragment>
    <div data-type='GroupByButtons'
         data-has-active-button={isCard && showingAggregateDetailsinSidebar}
         {..._.omit(props, ['levelOfDetail'])}
         onClick={onClick}
         className={classNames(css.buttons, className)}
    >
      {!isFlat && <ExpandCollapseButtons/>}
      {isCard && groupStore.catalog.settings.showAggs && <ToggleAggregationLocationButton className={classNames(css.groupAggregateToggle)}/>}
    </div>
  </Fragment>) : <RightGroupByButtons/>
})

export const FarRightFlatModeGroupByButtons = observer(function FarRightFlatModeGroupByButtons() {
  const store = useGroupByGroupStore(),
    {
      isExpanded, isFlat, infoCardProps: {level},
      hasChildGroup, parentStore, childGroupStore, groupBy, grouping: {showAddIcon}
    } = store,
    inColumnView = parentStore?.settings?.columnView

  if (!isFlat) return null

  return (
    <div className={classNames(css.showOnHover, css.farRightButtons)} data-column-view={inColumnView} data-type='FarRightGroupByButtons'>
      {isExpanded && <ToggleGroupSortButton/>}

      {isExpanded && !childGroupStore.isCard &&
	   <Fragment>
         {isExpanded && <FullHeightGroupButton/>}
	   </Fragment>}
      {!inColumnView && <FullWidthGroupButton/>}

      {/*{!childGroupStore.isCard && level > 0 && parentStore?.settings?.childColumns != 1 && <FullWidthGroupButton/>}*/}
      {DEBUG && isExpanded && isFlat && hasChildGroup && !_.isEmpty(childGroupStore.grouping.subtitleFields) && <ColumnViewFlatModeToggleButton/>}
      {isExpanded && level + 1 == groupBy.hierarchy.length && <Fragment>
		  <SingleLineGroupButton/>
	  </Fragment>}
      {false && hasChildGroup && !inColumnView && <ChildGroupsColumnSlider/>}
    </div>)
})

export const ColumnViewFlatModeToggleButton = observer(function ColumnViewFlatModeToggleButton() {
  const {strings, grouping: {toggle2DColumnView, settingsForLevel: {twoDColumnView}}} = useGroupByGroupStore()
  return <PrimaryIconButton icon='arrows-vertical' tooltipStyle='mui' tooltip={strings.groupedColumnView} active={twoDColumnView} onClick={toggle2DColumnView}/>
})

export const ToggleGroupSortButton = observer(function ToggleGroupSortButton() {
  const {strings, grouping: {toggleSortField}} = useGroupByGroupStore()
  return <PrimaryIconButton icon='sort' tooltipStyle='mui' tooltip={strings.sort} onClick={toggleSortField('_key')}/>
})

export const GroupInfoCardMoreMenuButton = observer<{ group?: any, path?: any, specStack, isUnassigned, isCustomAttribute?: boolean, items?: any, level?: number, className?: any }>(
  function GroupInfoCardMoreMenuButton({group, path, specStack, isUnassigned, items, isCustomAttribute, level, className}) {
    const {catalog: {mutations: {deactivateItems, unassign, deleteItems}}} = useStores()

    return <Popover2
      interactionKind='click'
      position='bottom-right'
      content={
        <Menu>
          <MenuItem data-id='deactivate-products' text='Deactivate Products' icon='exchange' onClick={() => confirm(`Are you sure you want to deactivate all products under this group?`) && deactivateItems(specStack, isCustomAttribute,
            items, isUnassigned, level)}/>
          {!isUnassigned && <MenuItem data-id='unassign-all-products' icon='ungroup-objects' text='Unassign All Products'
		                              onClick={() => confirm(`Are you sure you want to unassign all products under this group?`) && unassign(group, path, isCustomAttribute,
                                        items, level)}/>}
          <MenuItem data-id='delete-all-products' text='Delete All Products' icon='trash' onClick={() => confirm(`Are you sure you want to delete all products under this group?`) && deleteItems(specStack, isCustomAttribute,
            items, isUnassigned, level)}/>
        </Menu>}
    >
      <PrimaryIconButton className={className} icon={<MoreHoriz/>}/>
    </Popover2>
  })

const AddCatalogItemToGroupButton = observer(function AddCatalogItemToGroupButton() {
  const {strings, isCard, creatingItem, onCreateGroupCatalogItem, catalog: {catalogItemName, isShowingPerspective, canCreateItem}} = useGroupByGroupStore()
  return !isShowingPerspective && (
    <PrimaryIconButton
      //intent='primary'
      //className={css.newItem}
      //minimal={false}
      tooltip={creatingItem ? strings.creating : canCreateItem || true ? `New ${catalogItemName}` : strings.requireMerchCategory}
      tooltipPosition={isCard ? 'left' : 'bottom'}
      icon={creatingItem ? <Spinner intent='primary' size={12}/> : <Icon icon='plus'/>}
      //text={`New ${catalog.boardConfig.itemName}`}
      //  disabled={!canCreateItem || creatingItem}
      onClick={onCreateGroupCatalogItem}
      //onClick={onCreateItem}/>
    />
  )
})

export const ChildGroupsColumnSlider = observer(function ChildGroupsColumnSlider() {
  const groupStore = useGroupByGroupStore(),
    {strings, groupBy} = groupStore,
    columns = groupStore ? (groupStore.settings.childColumns ? groupStore.settings.childColumns : groupBy.individualGroupSettings.childColumns)
                         : groupBy.settings.individualGroupSettings.childColumns

  //const {settings: {childColumns}, onChangeColumns, onApplyColumnsToAllGroups, childGroupStore: {isCard}, props: {groupCount, level}} = useGroupByGroupStore()
  //if (mgr.groupCount <= 1) return null
  return groupStore?.isCard ? null :
    /*(groupStore && groupStore.props.level + 2 != groupBy.hierarchy.length && groupBy.hierarchy.length != 1) ||*/ (groupStore && !groupStore.isExpanded) ? null : (
      <div title={strings.numberOfColumns} className={classNames(groupStore && css.showOnHover, css.columnsSlider)}>
        <Slider max={groupBy.maxChildGroupColumns}
                min={1}
                value={columns ?? 1}
                onChange={groupStore?.onChangeColumns ?? groupBy.onChangeColumns}/>
        {false && groupStore && <Button text={strings.applyToAllGroups} onClick={groupStore.onApplyColumnsToAllGroups}/>}
      </div>
    )
})

const FullWidthGroupButton = observer(function FullWidthGroupButton() {
  const {strings, settings: {fullWidth}, onToggleFullWidth} = useGroupByGroupStore()
  return (
    <PrimaryIconButton
      tooltip={strings.fullWidth}
      tooltipStyle='mui'
      active={fullWidth}
      icon={<Icon icon='arrows-horizontal'/>}
      onClick={onToggleFullWidth}
    />
  )
})

const ColumnViewGroupButton = observer(function ColumnViewGroupButton() {
  const {
    strings, settings: {columnView}, onToggleColumnView
  } = useGroupByGroupStore()
  return (
    <PrimaryIconButton
      tooltip={strings.groupedColumnView}
      tooltipStyle='mui'
      active={columnView}
      icon={<Icon icon='two-columns'/>}
      onClick={onToggleColumnView}
    />
  )
})

const FullHeightGroupButton = observer(function FullHeightGroupButton() {
  const {
    strings, settings: {fullHeight}, onToggleFullHeight, hasChildGroup, props: {level}, groupBy
  } = useGroupByGroupStore()

  /**
   * This only is valid if we have grandchild groups
   */
  if (!hasChildGroup || level + 2 <= groupBy.hierarchy.length) return null

  return (
    <PrimaryIconButton
      tooltip={strings.fullHeight}
      tooltipStyle='mui'
      active={fullHeight}
      icon={<Icon icon='arrows-vertical'/>}
      onClick={onToggleFullHeight}
    />
  )
})

const SingleLineGroupButton = observer(function SingleLineGroupButton() {
  const {
    strings, settings: {singleLine}, onToggleSingleLine
  } = useGroupByGroupStore()
  return (
    <PrimaryIconButton
      tooltip={strings.singleLineOfProductCards}
      tooltipStyle='mui'
      active={singleLine}
      icon={<Icon icon='layout-linear'/>}
      onClick={onToggleSingleLine}
    />
  )
})

const ToggleExpandButton = observer(function ToggleExpandButton() {
  const {strings, isCard, stores: {sidebar}, groupBy, props: {level, hideChildren}, isExpanded, loading, onExpandButtonDragEnter, onExpandButtonDragLeave, onToggleExpandCollapse} = useGroupByGroupStore()

  return (
    <div className={classNames(css.toggleExpand, isCard && level > 0 && !hideChildren && css.horizontal, isExpanded && css.expanded)}>
      <PrimaryIconButton
        tooltipPosition={isCard ? 'right' : sidebar.left.open ? 'left' : 'bottom'}
        tooltip={loading
                 ? 'Loading...'
                 : pluralize(isExpanded ? strings.collapseGroups : strings.expandGroups, 1)}
        tooltipStyle='mui'
        onClick={onToggleExpandCollapse}
        onDragEnter={onExpandButtonDragEnter}
        onDragLeave={onExpandButtonDragLeave}
        icon={loading && isExpanded && !groupBy.pivotView
              ? <Spinner size={20}/>
              : <Icon icon={isCard && level > 0 ? 'chevron-right' : 'chevron-down'}/>} // These get rotated by css
      />
    </div>)
})

const ExpandCollapseButtons = observer(function ExpandCollapseButtons() {
  const {strings, stores: {sidebar}, isCard, onCollapseAll, onExpandAll, isExpanded, loading, showExpandCollapseAll} = useGroupByGroupStore()
  if (!showExpandCollapseAll) {
    return <ToggleExpandButton/>
  } else {
    return (
      <Fragment>
        <PrimaryIconButton
          tooltipPosition={isCard ? 'right' : sidebar.left.open ? 'left' : 'bottom'}
          tooltip={strings.collapseGroups}
          tooltipStyle='mui'
          disabled={!isExpanded}
          onClick={onCollapseAll}><Icon icon='double-chevron-up'/></PrimaryIconButton>

        <div>
          <PrimaryIconButton onClick={onExpandAll}
                             tooltipPosition={isCard ? 'right' : sidebar.left.open ? 'left' : 'bottom'}
                             wrapperClassName={css.toggleExpand}
                             tooltip={loading ? strings.groupLoading : `${strings.expandGroups}`}
                             disabled={loading}>
            {loading ? <Spinner size={20}/> : <Icon icon='double-chevron-down'/>}
          </PrimaryIconButton>
        </div>
      </Fragment>)
  }
})

const SpecDetailsButton = observer(function SpecDetailsButton() {
  const {isCard, library, showingSpecDetails, onToggleSpecDetails, groupLabel} = useGroupByGroupStore()

  return library ? (
        <PrimaryIconButton
          tooltipPosition={isCard ? 'left' : 'bottom'}
          tooltip={!showingSpecDetails ? `Show '${groupLabel}' Details` : `Hide '${groupLabel}' Details`}
          tooltipStyle='mui'
          data-name='spec-details'
          active={showingSpecDetails}
          className={classNames(css.rightActionButton)}
          onClick={onToggleSpecDetails}
          >
          <Icon icon={icons.library} iconSize={16}/>
        </PrimaryIconButton>
  ) : null
})

observer(() => {
  const {strings, grouping, props: {group, path}, onToggleQrCode} = useGroupByGroupStore()

  return (
    <PrimaryIconButton
      tooltip={strings.showQRCode}
      tooltipStyle='mui'
      active={grouping && grouping.isShowQrCode(group, path)}
      onClick={onToggleQrCode}><Icon icon='code'/></PrimaryIconButton>
  )
})

const SelectImagesFileInput = observer(function SelectImagesFileInput() {
  const {props: {info}, onSetSelectImagesFileInputRef, onSelectImages} = useGroupByGroupStore()

  return (
    <input
      type='file'
      id={info.key.toString()}
      ref={onSetSelectImagesFileInputRef}
      style={{display: 'none'}}
      multiple
      onChange={onSelectImages}
    />)
})

const CopyGroupByStoreToWindowVariable = observer(function CopyGroupByStoreToWindowVariable() {
  const store = useGroupByGroupStore(),
    {isCard, onCopyStoreToWindow} = store
  return (
      <PrimaryIconButton
        tooltip={`Copy Store to Window Object`}
        tooltipStyle='mui'
        tooltipPosition={isCard ? 'right' : 'bottom'}
        icon={icons.dev}
        onClick={onCopyStoreToWindow}
      />
  )
})

const CopyGroupByQueryToClipboardButton = observer(function CopyGroupByQueryToClipboardButton() {
  const store = useGroupByGroupStore(),
    {isCard, copyDbString, onCopyGroupByQueryToClipboard} = store
  return (
      <PrimaryIconButton
        icon={<Icon icon={copyDbString ? 'tick' : 'database'}/>}
        tooltipStyle='mui'
        onClick={onCopyGroupByQueryToClipboard}
        tooltip={`${copyDbString ? 'Copied' : 'Copy'} Group By Query to Clipboard`}
        tooltipPosition={isCard ? 'right' : 'bottom'}
      />
  )
})

const RightGroupByButtons = observer(function RightGroupByButtons() {
  const store = useGroupByGroupStore(),
    {strings, catalog: {canShowAggs, settings}, catalog, isCard, isFlat, isExpanded, grouping: {showAddIcon, fieldDefinition}, esInfo, hasInlineCollapsedCatalogItems, onCollapseAllLevelOfDetail, levelOfDetail, onShowLevelOfDetail, hasInlineExpandedCatalogItems} = store,
    libraryForField = catalog.libraryByIndex[fieldDefinition.esIndex]

  return (
    <Fragment>
      <SelectImagesFileInput/>
      <div data-type='GroupByButtons'
        //data-has-active-button={(isCard && levelOfDetail == 1)}
           onClick={utils.eatMouseEvent}
           data-align='right'
           className={classNames(css.buttons, css.showOnHover)}
      >
        {isFlat && <ExpandCollapseButtons/>}
        <SpecDetailsButton/>

        {showAddIcon === true && <AddCatalogItemToGroupButton/>}


        {/*<GroupInfoCardMoreMenuButton group={group} path={path} specStack={specStack} isUnassigned={isUnassigned} items={items} isCustomAttribute={isCustomAttribute} level={level}/>*/}

        {isExpanded &&
         <Fragment>
	         <PrimaryIconButton className={classNames(css.rightActionButton)}
	                            tooltipStyle='mui'
	                            tooltip={strings.levelOfDetail(1)}
	                            tooltipPosition={isCard ? 'left' : 'bottom'}
	                            disabled={!(hasInlineExpandedCatalogItems || levelOfDetail === 1)}
	                            icon={<Icon icon={'zoom-out'}/>}
	                            onClick={onCollapseAllLevelOfDetail}/>

	         <PrimaryIconButton className={classNames(css.rightActionButton)}
	                            tooltip={strings.levelOfDetail(0)}
	                            tooltipPosition={isCard ? 'left' : 'bottom'}
	                            tooltipStyle='mui'
	                            disabled={!(levelOfDetail !== 1 || hasInlineCollapsedCatalogItems)}
	                            icon={<Icon icon={'zoom-in'}/>}
	                            onClick={onShowLevelOfDetail}/>
         </Fragment>}

        {canShowAggs && <InlineGroupAggregateToggleButton className={classNames(css.groupAggregateToggle)}/>}
        {isFlat && canShowAggs && <ToggleAggregationLocationButton className={classNames(css.groupSidebarAggregate)}/>}


        {isCard && esInfo && libraryForField &&
		 <Popover2
			 interactionKind='click'
			 position='auto'
			 hoverOpenDelay={300}
			 content={<BuilderCardLoader hideButtons isPopover levelOfDetail={1} nested={{data: esInfo, field: fieldDefinition}} id={catalog.idFor(esInfo)}/>}
		 >
				 <PrimaryIconButton
			 tooltip={strings.itemDetails}
			 tooltipPosition={isCard ? 'left' : 'bottom'}
	   tooltipStyle='mui'
	   icon={<Icon icon='style'/>}/>
		</Popover2>
        }
      </div>
    </Fragment>
  )
})



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/GroupByCatalog_LoadMore/GroupByButtons.tsx